// src/pages/roles/RoleList.tsx
import React, { useMemo, useRef } from 'react';
import { Button } from 'antd';
import { AgGridReact } from 'ag-grid-react';

import { PLGrid } from '../../components/common/PLGrid';
import { GridResetButton } from '../../components/common/GridResetButton';
import ExportAgCSVButton from '../../components/common/ExportAgCSVButton';
import { getActionColumn } from '../../libs/getActionColumn';
import { serialNumberColumn } from '../../libs/getSerialNumberColumn';
import { useGridData } from '../../hooks/useGridData';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';

import {
  roleConfig, 
} from './role.config';
import { useNavigate } from 'react-router-dom';
import { Role } from '../../types/roles.types';
import BulkUploadButton from '../../components/common/BulkUploadButton';

const RoleList: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const { rowData, loading, refetch } = useGridData<Role>(roleConfig.api.fetchAll);
  const deleteHandler = useDeleteHandler(); 
  const navigate = useNavigate();

  const actionColumn = useMemo(() =>
    getActionColumn<Role>({
      viewUrl: row => `${roleConfig.redirectPath}/view/${row._id}`, // ✅ Pass view URL
      editUrl: row => `${roleConfig.redirectPath}/edit/${row._id}`, // ✅ Pass edit URL
      onDelete: row => deleteHandler({
        id: row._id,
        entityName: roleConfig.entityName,
        deleteApi: roleConfig.api.delete,
        onSuccess: () => refetch()
      }),
    }), [deleteHandler]
  );

  const columnDefs = useMemo(() => [
    serialNumberColumn,
    ...roleConfig.roleTableColumns,
    actionColumn,
  ], [actionColumn]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold m-0">{roleConfig.entityName} List</h2>
        <div className="flex items-center gap-2">
          <GridResetButton gridRef={gridRef} />
          <ExportAgCSVButton gridRef={gridRef} fileName={`${roleConfig.entityName}List`} />
          <BulkUploadButton redirectPath={`${roleConfig.redirectPath}/excel-upload`} />
          <Button type="primary" onClick={() => navigate(`${roleConfig.redirectPath}/create`)}>
            Create {roleConfig.entityName}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto">
        <PLGrid
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default RoleList;
